/**
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { Fragment, useRef, useLayoutEffect, useContext, type PropsWithChildren } from 'react';
import { ProfilerContext } from '../../profiler';
import { WaitContext } from '../../lazy-wait';
import { LazySuspenseContext } from '../../suspense';

function isLinkPrefetch(el: HTMLElement): el is HTMLLinkElement {
	return el.tagName === 'LINK' && (el as HTMLLinkElement).rel === 'prefetch';
}

const usePlaceholderRender = (resolveId: string, content: HTMLElement[]) => {
	const hydrationRef = useRef<HTMLInputElement | null>(null);
	const { current: ssrDomNodes } = useRef(content || ([] as HTMLElement[]));
	const profilerContext = useContext(ProfilerContext).current;
	const waitContext = useContext(WaitContext);
	const suspenseContext = useContext(LazySuspenseContext);

	useLayoutEffect(() => {
		const element = hydrationRef.current;
		const { parentNode } = element || {};

		profilerContext?.placeholderFallBackMounted(
			resolveId,
			suspenseContext.name,
			!waitContext.currentValue(),
		);

		if (parentNode && !parentNode.contains(ssrDomNodes[0])) {
			ssrDomNodes.reverse().forEach((node: HTMLElement) => {
				// this fixes an issue with Chrome that re-triggers and cancels prefetch
				// when node is appended again, making network panel quite noisy
				if (isLinkPrefetch(node)) {
					node.rel = '';
				}

				parentNode.insertBefore(node, (element as any).nextSibling);
			});
		}

		return () => {
			profilerContext?.placeholderFallBackUnmounted(
				resolveId,
				suspenseContext.name,
				!waitContext.currentValue(),
			);
			ssrDomNodes.forEach((node: HTMLElement) => node.parentNode?.removeChild(node));
		};
		// [hydrationRef.current, ssrDomNodes] are expected to be stable
		// with the second one never changing by design and the first one changing after first render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return hydrationRef;
};

const displayContents = css({
	display: 'contents',
});

const HTMLPlaceholderFallbackRender = ({ content }: { content: string }) => {
	return <div css={displayContents} dangerouslySetInnerHTML={{ __html: content }} />;
};

const ElementPlaceholderFallbackRender = ({
	id,
	content,
}: {
	content: HTMLElement[];
	id: string;
}) => {
	const placeholderRef = usePlaceholderRender(id, content);

	return <input type="hidden" data-lazy-begin={id} ref={placeholderRef} />;
};

export type PlaceholderFallbackRenderProps = {
	content: string | HTMLElement[];
	id: string;
};

export const WithHTMLPlaceholder = ({
	enabled,
	children,
}: PropsWithChildren<{ enabled: boolean }>): JSX.Element => {
	if (!enabled) {
		return <Fragment>{children}</Fragment>;
	}
	return <div css={displayContents}>{children}</div>;
};

export const PlaceholderFallbackRender = ({
	id,
	content,
}: PlaceholderFallbackRenderProps): JSX.Element => {
	return typeof content === 'string' ? (
		<HTMLPlaceholderFallbackRender content={content} />
	) : (
		<ElementPlaceholderFallbackRender id={id} content={content} />
	);
};
